import { FC, useCallback, useMemo } from 'react';
import styles from './SamplePlanTable.module.scss';
import { GroupTarget, PlannedAnimalTarget, RegisteredAnimalTarget } from '@/model/SamplePlan.model';
import {
  optionSet,
  SampleDefinitionRowData,
  SamplePlanTableContext,
  TableOptions,
  UnassignedGroup,
  useSamplePlanTableContext,
} from '@/components/Samples/SamplePlanTable/SamplePlanTableProvider';
import { setsIntersection } from '@/utils/browser.utils';
import { _notNil } from '@/littledash';
import { DetailCell } from './DetailCell';
import { MetadataCell } from './MetadataCell';
import { CalculateTimepointCell } from './CalculateTimepointCell';
import { isApiId } from '@/utils/ApiId.utils';

const PlannedAnimalId: FC<{ api_id: RegisteredAnimalTarget['api_id'] }> = ({ api_id }) => {
  const { registered_animal_api_id_to_planned_animal_api_id, animal_map } = useSamplePlanTableContext();
  return useMemo(() => {
    if (isApiId('aml', api_id, false)) {
      const plannedAnimalApiId = registered_animal_api_id_to_planned_animal_api_id.get(api_id);
      const animal = _notNil(plannedAnimalApiId) ? animal_map?.get(plannedAnimalApiId) : null;
      return animal?.type === 'planned' ? (animal?.planned_id ?? null) : null;
    }
    return null;
  }, [api_id, registered_animal_api_id_to_planned_animal_api_id, animal_map]);
};

const AnimalRow: FC<{
  animal_api_id: RegisteredAnimalTarget['api_id'] | PlannedAnimalTarget['api_id'];
  data: SampleDefinitionRowData;
}> = ({ animal_api_id, data }) => {
  const { animal_map, active_details, active_metadata, table_options } = useSamplePlanTableContext();
  const animal = useMemo(() => animal_map.get(animal_api_id), [animal_map, animal_api_id]);
  switch (animal?.type) {
    case 'planned': {
      if (_notNil(animal.animal_api_id)) {
        return null;
      }
      return (
        <div className={styles.AnimalRow} data-test-component="AnimalRow" data-test-element="container">
          {optionSet(TableOptions.display_selection_column, table_options) ? <div></div> : null}
          <div className={styles.ChevronCell}></div>
          <div></div>
          <div></div>
          {optionSet(TableOptions.display_planned_animal_column, table_options) ? (
            <div data-test-element="planned-id">{animal.planned_id}</div>
          ) : null}
          {optionSet(TableOptions.display_registered_animal_column, table_options) ? <div></div> : null}
          {optionSet(TableOptions.collected_samples_count, table_options) ? (
            <>
              <div></div>
              <div></div>
            </>
          ) : null}
          <div>{data.sample_type_name}</div>
          <div>{data.timepoints}</div>
          {optionSet(TableOptions.hide_calculate_collection_time_column, table_options) ? null : (
            <div>
              <CalculateTimepointCell value={data.calculate_collection_time} />
            </div>
          )}
          {Array.from(active_details).map((detail) => (
            <DetailCell key={detail} detail={data.details?.[detail]} />
          ))}
          {Array.from(active_metadata).map((api_id) => (
            <MetadataCell key={api_id} type={api_id} value={data.metadata?.[api_id]} />
          ))}
        </div>
      );
    }
    case 'registered': {
      return (
        <div
          className={styles.AnimalRow}
          data-test-component="AnimalRow"
          data-test-element="container"
          data-test-key={animal.api_id}
        >
          {optionSet(TableOptions.display_selection_column, table_options) ? <div></div> : null}
          <div className={styles.ChevronCell}></div>
          <div></div>
          <div></div>
          {optionSet(TableOptions.display_planned_animal_column, table_options) ? (
            <div data-test-element="planned-id">
              <PlannedAnimalId api_id={animal.api_id} />
            </div>
          ) : null}
          {optionSet(TableOptions.display_registered_animal_column, table_options) ? (
            <div data-test-element="name">{animal.name}</div>
          ) : null}
          {optionSet(TableOptions.collected_samples_count, table_options) ? (
            <>
              <div></div>
              <div></div>
            </>
          ) : null}
          <div>{data.sample_type_name}</div>
          <div>{data.timepoints}</div>
          {optionSet(TableOptions.hide_calculate_collection_time_column, table_options) ? null : (
            <div>
              <CalculateTimepointCell value={data.calculate_collection_time} />
            </div>
          )}
          {Array.from(active_details).map((detail) => (
            <DetailCell key={detail} detail={data.details?.[detail]} />
          ))}
          {Array.from(active_metadata).map((api_id) => (
            <MetadataCell key={api_id} type={api_id} value={data.metadata?.[api_id]} />
          ))}
        </div>
      );
    }
    default:
      return null;
  }
};

/**
 * Returns a string key for sorting animals.
 * - For planned animals, returns their `planned_id`.
 * - For registered animals, returns the associated planned animal's `planned_id` if available,
 *   otherwise returns the registered animal's name.
 * - Returns an empty string for unknown types.
 *
 * @param animalApiId - The API ID of the animal (planned or registered).
 * @param animal_map - Map of animal API IDs to animal objects.
 * @param registered_animal_api_id_to_planned_animal_api_id - Map of registered animal API IDs to planned animal API IDs.
 * @returns A string used for sorting animals.
 */
const animalSortKey = (
  animalApiId: PlannedAnimalTarget['api_id'] | RegisteredAnimalTarget['api_id'],
  animal_map: SamplePlanTableContext['animal_map'],
  registered_animal_api_id_to_planned_animal_api_id: SamplePlanTableContext['registered_animal_api_id_to_planned_animal_api_id']
): string => {
  const animal = animal_map.get(animalApiId);
  switch (animal?.type) {
    case 'planned': {
      return animal.planned_id;
    }
    case 'registered': {
      const plannedAnimalId = isApiId('aml', animalApiId, false)
        ? registered_animal_api_id_to_planned_animal_api_id.get(animalApiId)
        : null;
      if (_notNil(plannedAnimalId)) {
        const plannedAnimal = animal_map.get(plannedAnimalId);
        if (plannedAnimal?.type === 'planned') {
          return plannedAnimal.planned_id;
        }
      }
      return animal.name;
    }
    default: {
      return '';
    }
  }
};

export const AnimalRows: FC<{
  data: SampleDefinitionRowData;
  group_api_id: GroupTarget['api_id'] | UnassignedGroup;
}> = ({ data, group_api_id }) => {
  const { group_animals, sample_definition_animals, animal_map, registered_animal_api_id_to_planned_animal_api_id } =
    useSamplePlanTableContext();
  const animalComparator = useCallback(
    (
      left: PlannedAnimalTarget['api_id'] | RegisteredAnimalTarget['api_id'],
      right: PlannedAnimalTarget['api_id'] | RegisteredAnimalTarget['api_id']
    ): number =>
      animalSortKey(left, animal_map, registered_animal_api_id_to_planned_animal_api_id).localeCompare(
        animalSortKey(right, animal_map, registered_animal_api_id_to_planned_animal_api_id),
        undefined,
        { numeric: true }
      ),
    [animal_map, registered_animal_api_id_to_planned_animal_api_id]
  );

  const animalApiIds = useMemo<Array<RegisteredAnimalTarget['api_id'] | PlannedAnimalTarget['api_id']>>(() => {
    const sampleDefinitionAnimals = sample_definition_animals.get(data.api_id);
    if (_notNil(sampleDefinitionAnimals)) {
      const groupAnimals = group_animals.get(group_api_id);
      const intersection = _notNil(groupAnimals)
        ? Array.from(setsIntersection(sampleDefinitionAnimals, groupAnimals)).sort(animalComparator)
        : [];

      return intersection;
    }
    return [];
  }, [data.api_id, group_api_id, group_animals, sample_definition_animals, animalComparator]);
  return (
    <>
      {animalApiIds.map((animal_api_id) => (
        <AnimalRow key={animal_api_id} animal_api_id={animal_api_id} data={data} />
      ))}
    </>
  );
};
